import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useForm, FormProvider, useWatch, useFieldArray } from "react-hook-form";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  EntityType,
  PaymentService,
  TenantService,
  PaidFlowTypesEnum,
  PayeeAccountTypesEnum,
  PaymentDueAccountVM,
  OrganizationService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import { UserLoginData } from "../../components/common/charts/Enums";
import InputField from "../form-fields/InputField";
import CurrencyInput from "../form-fields/CurrencyInput";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import SelectField from "../form-fields/SelectField";
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import { Accordion, AccordionSection } from "../card/accordion";
import SimpleOutlineButton from "../buttons/Simple-Outline-button";
import DocumentUploader from "../CustomUploader/DocumentUploader";
import SvgRoundIconButton from "../buttons/Svg-Round-Icon-button";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import DynamicTabs from "../tabs/DynamicTabs";
import DuesORTransaction from "./DuesORTransaction";
import './payment.css';
import SimpleSpinner from "../../components/loader";
import EmptyData from "../../components/common/EmptyData";
import { GetImages } from "../../utils/GetImages";
import { utcDateFormat } from "../../utils/helper";
import ReceivableDetailCard from "../listings/listing-cards/receivable-detail";
import SimpleButton from "../buttons/Simple-button";
import PaymentDashboard from "./PaymentDashboard";
import { t } from "i18next";
import { tr } from "date-fns/locale";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface PaymentItem {
  id?: number;
  PaymentType: string;
  Amount: string;
  DueDate: string;
  GracePeriod: string;
  Description: string;
}

interface FormValues {
  tenantId?: string;
  documentfile?: File | null;
  AccountNumber?: string;
  InvoiceNo?: string;
  payments: PaymentItem[];
}

interface AccordionState {
  basicDetailsOpen: boolean;
  paymentInfoOpen: boolean;
}

interface ModalState {
  viewAll: boolean;
}

interface ApiData {
  tenants: any[];
  paymentTypes: any[];
  dues: PaymentDueAccountVM[];
  transactions: any[];
  selectedReceivableDetail: PaymentDueAccountVM | null;
}

interface EditReceivableProps {
  fetchData?: () => void;
  isEntityProp?: boolean;
  entityIdProp?: number;
  entityTypeIdProp?: EntityType;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_ACCORDION_STATE: AccordionState = {
  basicDetailsOpen: true,
  paymentInfoOpen: false,
};

const INITIAL_MODAL_STATE: ModalState = {
  viewAll: false,
};

const DEFAULT_PAYMENT_ITEM: PaymentItem = {
  id: undefined,
  PaymentType: "",
  Amount: "",
  DueDate: "",
  GracePeriod: "",
  Description: "",
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EditReceivable = ({ fetchData, isEntityProp, entityIdProp, entityTypeIdProp }: EditReceivableProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const urlTenantId = searchParams.get("tenantId");
  const urlPaymentType = searchParams.get("paymentType");
  const [payeeAccountId, setPayeeAccountId] = useState<number>(0);

  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  const [resetCounter, setResetCounter] = useState(0);
  const [modals, setModals] = useState<ModalState>(INITIAL_MODAL_STATE);
  const [data, setData] = useState<ApiData>({
    tenants: [],
    paymentTypes: [],
    dues: [],
    transactions: [],
    selectedReceivableDetail: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHeader, setIsLoadingHeader] = useState(true);
  const [isDuesLoading, setIsDuesLoading] = useState(false);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [accordionState, setAccordionState] = useState<AccordionState>(INITIAL_ACCORDION_STATE);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDueAccountVM | null>(null);
  const [originalPaymentIds, setOriginalPaymentIds] = useState<number[]>([]);
  const [originalDocumentId, setOriginalDocumentId] = useState<number | null>(null);

  const isEntity = isEntityProp ?? (searchParams.get('isEntity') === 'true');
  const entityId = entityIdProp ?? (Number(searchParams.get('entityId')) || undefined);
  const entityTypeId = entityTypeIdProp ?? (Number(searchParams.get('entityTypeId')) || undefined);

  const backParams = "?paymentMode=Receivables&paidFlowTypeId=2";

  const backPath = useMemo(() => {
    if (isEntity && entityId && entityTypeId) {
      if (entityTypeId === EntityType.Property) return `/properties/propertydetails/${entityId}`;
      if (entityTypeId === EntityType.Unit) return `/units/unitdetails/${entityId}`;
    }
    return "/payment";
  }, [isEntity, entityId, entityTypeId]);

  // Form Setup
  const methods = useForm<FormValues>({
    defaultValues: {
      tenantId: urlTenantId || "",
      documentfile: null,
      AccountNumber: "",
      InvoiceNo: "",
      payments: [DEFAULT_PAYMENT_ITEM],
    },
    mode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments",
  });

  const tenantId = useWatch({ control, name: "tenantId" });

  // ============================================================================
  // API CALLS
  // ============================================================================

  const fetchDocument = async (paymentId: number) => {
    try {
      const res = await apiHelper(
        DocumentService.getApiDocumentBase64,
        { showNotification: false },
        paymentId,
        DocumentEntityTypesEnum.Payment,
        DocumentTypesEnum.PaymentReference
      );

      const items = res?.data?.success;
      if (items && items.length > 0) {

        const doc = items[0];
        setOriginalDocumentId(doc.id);
        // Convert Base64 string to File object
        const byteCharacters = atob(doc.base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        return new File([blob], doc.fileName || "invoice.pdf", { type: "application/pdf" });
      }
    } catch (error) {
      console.error("Failed to fetch document", error);
    }
    setOriginalDocumentId(null);
    return null;
  };

  const fetchTenants = async () => {
    try {
      const res = await apiHelper(TenantService.getApiTenantOrganizationlookup, { showNotification: false }, organizationId);
      setData(prev => ({ ...prev, tenants: res?.data?.success || [] }));

    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch tenants",
        type: NotificationType.Failure,
      });
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const res = await apiHelper(
        PaymentService.getApiPayeeAccountPaymentTypes,
        { showNotification: false },
        PayeeAccountTypesEnum.Tenant
      );
      setData(prev => ({ ...prev, paymentTypes: res?.data?.success || [] }));

    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch payment types",
        type: NotificationType.Failure,
      });
    }
  };

  const fetchTenantDues = useCallback(async (tid: string) => {
    if (!tid) return;
    setIsDuesLoading(true);
    try {
      const res = await apiHelper(
        PaymentService.getApiInvoicePayeeAccountTenantList,
        { showNotification: false },
        Number(tid)
      );

      const dues = res?.data?.success || [];
      setData(prev => ({ ...prev, dues }));

      // Auto-select first due if none selected
      if (dues.length > 0 && !selectedPayment) {
        handlePaymentSelect(dues[0]);
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch dues",
        type: NotificationType.Failure,
      });
    } finally {
      setIsDuesLoading(false);
      setIsLoadingHeader(false);
    }
  }, [selectedPayment]);

  const fetchTransactions = useCallback(async () => {
    if (!tenantId) return;
    setIsTransactionsLoading(true);
    try {
      const res = await apiHelper(
        OrganizationService.getApiOrganizationTransactionOrganizationlist,
        { showNotification: false },
        organizationId,
        5,
        1,
        undefined,
        undefined,
        undefined,
        `TenantId = ${tenantId}`,
        "PaymentDate desc"
      );
      setData(prev => ({ ...prev, transactions: res?.data?.success || [] }));
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch transactions",
        type: NotificationType.Failure,
      });
    } finally {
      setIsTransactionsLoading(false);
    }
  }, [tenantId, organizationId]);

  const loadInvoiceItems = useCallback(async (referenceNumber: string, tenantId: string, paymentId?: number) => {
    try {
      const res = await apiHelper(
        PaymentService.getApiPaymentDueAccountInvoiceitems,
        { showNotification: false },
        referenceNumber
      );
      const items = res?.data?.success || [];
      if (items.length === 0) return null;

      const firstItem = items[0];
      let fetchedFile: File | null = null;

      // Fetch document if paymentId is provided
      if (paymentId) {
        fetchedFile = await fetchDocument(paymentId);
      }

      const formData: FormValues = {
        tenantId,
        documentfile: fetchedFile,
        AccountNumber: firstItem.accountNumber || "",
        InvoiceNo: firstItem.referenceNumber || "",
        payments: items.map((item: any) => ({
          id: item.id,
          PaymentType: String(item.paymentTypeId),
          Amount: String(item.amount),
          DueDate: DateHelper.convertUTCtoDateTime2(item.dueDate),
          GracePeriod: String(item.gracePeriodInDays || 0),
          Description: item.description || "",
        })),
      };

      return formData;
    } catch (error) {
      console.error("Failed to load invoice items:", error);
      return null;
    }
  }, []);

  const fetchReceivableDetail = async (tenantId: string) => {
    try {
      const res = await apiHelper(
        PaymentService.getApiInvoicePayeeAccountTenantHeader,
        { showNotification: false },
        Number(tenantId)
      );


      setData(prev => ({ ...prev, selectedReceivableDetail: res?.data?.success || null }));

      setPayeeAccountId(res?.data?.success?.payeeAccountId || 0);
    } catch (error) {
      console.error("Failed to fetch receivable detail", error);
    }
  };

  // ============================================================================
  // HANDLERS & EFFECTS
  // ============================================================================

  const toggleModal = (modalName: keyof ModalState) => {
    setModals(prev => ({ ...prev, [modalName]: !prev[modalName] }));
  };

  const toggleAccordion = (section: keyof AccordionState) => {
    setAccordionState(prev => ({
      basicDetailsOpen: section === 'basicDetailsOpen',
      paymentInfoOpen: section === 'paymentInfoOpen',
    }));
  };

  const handlePaymentSelect = async (payment: PaymentDueAccountVM) => {
    setSelectedPayment(payment);
    if (!payment.referenceNumber) return;

    try {
      // Pass payment.id to loadInvoiceItems to fetch the document
      const formData = await loadInvoiceItems(payment.referenceNumber, tenantId, payment.id);
      if (formData) {
        const loadedIds = formData.payments
          .map(p => p.id)
          .filter((id): id is number => !!id && id > 0);
        setOriginalPaymentIds(loadedIds);
        reset(formData);
        setAccordionState({ basicDetailsOpen: true, paymentInfoOpen: true });
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load payment details",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openPaymentInfo = () => {
    setAccordionState({ basicDetailsOpen: false, paymentInfoOpen: true });
  };

  const createPaymentObject = (formData: FormValues, item: PaymentItem, entityId: number) => ({
    amount: Number(item.Amount),
    description: item.Description,
    dueDate: utcDateFormat(item.DueDate),
    entityId,
    entityTypeId: EntityType.Tenant,
    accountNumber: formData.AccountNumber,
    payeeAccountId: payeeAccountId,
    gracePeriodInDays: Number(item.GracePeriod) || 0,
    referenceNumber: formData.InvoiceNo,
    paymentTypeId: Number(item.PaymentType) || 0,
    paidFlowTypeId: PaidFlowTypesEnum.Receiveable,
    organizationId,
  });

  const uploadDocument = async (paymentId: number, file: File) => {
    await apiHelper(DocumentService.postApiDocument, { showNotification: false }, {
      EntityTypeId: DocumentEntityTypesEnum.Payment,
      EntityId: paymentId,
      DocumentTypeId: DocumentTypesEnum.PaymentReference,
      IsMain: true,
      FileName: file.name,
      Document: new Blob([file]),
    });
  };

  const handlePaymentUpdate = async (formData: FormValues) => {
    setIsLoading(true);
    try {
      const entityId = Number(formData.tenantId);

      const currentIds = new Set(
        formData.payments
          .map(p => p.id)
          .filter((id): id is number => !!id && id > 0)
      );
      const idsToDelete = originalPaymentIds.filter(id => !currentIds.has(id));

      const deletePromises = idsToDelete.map(id =>
        apiHelper(PaymentService.deleteApiPaymentDueAccount, { showNotification: false }, id)
      );

      const updateCreatePromises = formData.payments.map(async (item) => {
        const paymentObj = createPaymentObject(formData, item, entityId);
        const isExisting = item.id && item.id > 0;

        const apiMethod = isExisting
          ? PaymentService.putApiPaymentDueAccount
          : PaymentService.postApiPaymentDueAccount;

        const payload = isExisting ? { ...paymentObj, id: item.id } : paymentObj;

        const res = await apiHelper(apiMethod, { showNotification: false }, payload);

        if (res?.data?.success && formData.documentfile) {
          await uploadDocument(res.data.success.id, formData.documentfile);
        }
      });

      await Promise.all([...deletePromises, ...updateCreatePromises]);

      // ─── NEW: Delete old document if user removed it ───────────────────────
      if (originalDocumentId && !formData.documentfile) {
        try {
          await apiHelper(
            DocumentService.deleteApiDocument,
            { showNotification: false },
            originalDocumentId
          );
          setOriginalDocumentId(null);
        } catch (deleteErr) {
          console.warn("Failed to delete old document:", deleteErr);
          // Usually safe to continue — don't fail the whole update
        }
      }

      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Payments updated successfully",
        type: NotificationType.Success,
        title: "Payment Updated",
        icon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
      });

      // Reset form
      reset({
        tenantId: formData.tenantId,
        documentfile: null,
        AccountNumber: "",
        InvoiceNo: "",
        payments: [DEFAULT_PAYMENT_ITEM],
      });

      setResetCounter(prev => prev + 1);
      setOriginalPaymentIds([]);
      fetchData?.();
      navigate(`${backPath}${backParams}`);
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to update payment",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => navigate(`${backPath}${backParams}`);
  const addPaymentRow = () => append({ ...DEFAULT_PAYMENT_ITEM });
  const removePaymentRow = (index: number) => remove(index);

  const handleTabChange = (tabId: string) => {
    if (tabId === "dues") fetchTenantDues(tenantId);
    else if (tabId === "transactions") fetchTransactions();
  };

  // Effects
  useEffect(() => {
    fetchTenants();
    fetchPaymentTypes();
  }, []);

  useEffect(() => {
    if (tenantId) {
      fetchTenantDues(tenantId);
      fetchReceivableDetail(tenantId);
    }
  }, [tenantId]);

  useEffect(() => {
    if (urlTenantId) {
      setValue("tenantId", urlTenantId);
    }
  }, [urlTenantId, setValue]);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  const tenantOptions = useMemo(() => data.tenants.map(t => ({ Id: t.id, Name: t.name })), [data.tenants]);
  const paymentTypeOptions = useMemo(() => data.paymentTypes.map(t => ({ Id: String(t.id), Name: t.name })), [data.paymentTypes]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderBasicDetails = () => (
    <Accordion>
      <AccordionSection title="Basic Details" isOpen={accordionState.basicDetailsOpen} onToggle={() => toggleAccordion('basicDetailsOpen')}>
        <div className="accordion-body">
          <div className="row">
            <div className="col-md-12">
              <form>
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <SelectField
                      placeholderText="Select Tenant"
                      name="tenantId"
                      label="Tenant"
                      required
                      options={tenantOptions}
                      isSearchable={true}
                    />
                  </div>
                  {tenantId && (
                    <div className="col-md-6">
                      <label className="fw-600 mt-md-4 w-100 mt-lg-4 mt-0 d-block"> </label>
                      <SimpleOutlineButton
                        style={{ paddingTop: 9, paddingBottom: 9 }}
                        className="w-100 mt-1"
                        type="button"
                        label="View All Dues & Transactions"
                        onClick={() => toggleModal('viewAll')}
                      />
                    </div>
                  )}
                </div>
                <div className="row mt-2">
                  <div className="col-md-4">
                    <InputField
                      name="AccountNumber"
                      label="Account Number"
                      required
                      type="text"
                      placeholder="Enter Account Number"
                      validation={{
                        maxLength: {
                          value: 50,
                          message: "Account number cannot exceed 50 characters"
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4 d-none">
                    <InputField
                      name="InvoiceNo"
                      label="Invoice No./Bill No."
                      type="text"
                      placeholder="Enter Invoice No./Bill No."
                      validation={{
                        maxLength: {
                          value: 100,
                          message: "Invoice number cannot exceed 100 characters"
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="fw-600">
                        Upload Bill / Invoice <span className="optionalLabel">(optional)</span>
                      </label>
                      <DocumentUploader name="documentfile" maxSizeMB={5} resetTrigger={resetCounter} />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <SimpleButton type="button" label="Next" onClick={openPaymentInfo} />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </AccordionSection>
    </Accordion>
  );

  const renderPaymentInfo = () => (
    <Accordion>
      <AccordionSection title="Payment Information" isOpen={accordionState.paymentInfoOpen} onToggle={() => toggleAccordion('paymentInfoOpen')}>


        <div className="accordion-body">
          {fields.map((field, index) => (
            <div className="payment-row" key={field.id}>
              <div className="payment-field">
                <SelectField
                  placeholderText="Select payment type"
                  name={`payments.${index}.PaymentType`}
                  label={index === 0 ? "Payment Type" : ""}
                  required={true}
                  requiredMessage="Payment Type is required"
                  options={paymentTypeOptions}
                  isAsterisk={index === 0}
                />
              </div>
              <div className="payment-field">
                <CurrencyInput
                  name={`payments.${index}.Amount`}
                  label={index === 0 ? "Amount" : ""}
                  required={true}
                  requiredMessage="Amount is required"
                  placeholder="Enter Amount"
                  disabled={isLoading}
                />
              </div>
              <div className="payment-field">
                <InputField
                  name={`payments.${index}.DueDate`}
                  label={index === 0 ? "Due Date" : ""}
                  type="date"
                  required={true}
                  requiredMessage="Due Date is required"
                  isAsterisk={index === 0}
                />
              </div>
              <div className="payment-field">
                <InputField
                  name={`payments.${index}.GracePeriod`}
                  label={index === 0 ? "Grace Period (Days)" : ""}
                  required={true}
                  requiredMessage="Grace Period is required"
                  isAsterisk={index === 0}
                  type="number"
                  placeholder="Enter Grace Period"
                  validation={{
                    min: { value: 0, message: "Grace period cannot be negative" },
                    maxLength: {
                      value: 2,
                      message: "Grace period cannot exceed 2 digits"
                    }
                  }}
                />
              </div>
              <div className="payment-field description-field">
                <InputField
                  name={`payments.${index}.Description`}
                  label={index === 0 ? "Description" : ""}
                  type="text"
                  placeholder="Enter description"
                  required={true}
                  isAsterisk={index === 0}
                  validation={{
                    maxLength: {
                      value: 250,
                      message: "Description cannot exceed 250 characters"
                    }
                  }}
                />
              </div>
              <div className="payment-action">
                {fields.length > 1 && (
                  <SvgRoundIconButton
                    style={{ width: 40, background: "#FDE8E8" }}
                    Icon={<DeleteRedIcon fill="#E02424" width="22" height="22" />}
                    onClick={() => removePaymentRow(index)}
                  />
                )}
              </div>
            </div>
          ))}
          <div className="mt-3">
            <SimpleOutlineButton type="button" label="Add More" onClick={addPaymentRow} />
          </div>
        </div>
      </AccordionSection>
    </Accordion>
  );

  const renderTransactionsContent = () => {
    if (isTransactionsLoading) return <div className='loading-card'><SimpleSpinner myClassName='Icon40 mt-5' /></div>;
    if (data.transactions.length === 0) return <EmptyData mainText="No Transactions" ImageSrc={GetImages.DuePayments} />;
    return data.transactions.map(item => <DuesORTransaction key={item.id} item={item} />);
  };


  if (isLoading) {
    return (
      <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }


  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
      <div className="d-flex flex-column gap-2 full-page-content edit-payment-container">
        <ReceivableDetailCard
          Id="ReceivableDetailCard"
          data={data.selectedReceivableDetail}
          loading={isLoadingHeader}
          isBorderRadius={false}
          IsFooter={true}
        />
        {
          data.selectedReceivableDetail && (
            <>
              <LeftArrowTopHeader title="Edit Receivable" backPath={`${backPath}${backParams}`} />


              <Card className="no-bg p-0 mt-0">
                <div className="edit-payment-content">
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <PaymentDashboard
                        payments={data.dues}
                        selectedPayment={selectedPayment}
                        onPaymentSelect={handlePaymentSelect}
                      />
                    </div>
                    <div className="col-md-8">

                      <FormProvider {...methods}>
                        <div className="d-flex flex-column gap-3">
                          {isLoading ? (
                            <SimpleSpinner myClassName="Icon40 mt-5" />
                          ) : (
                            <>
                              {renderBasicDetails()}
                              {renderPaymentInfo()}
                            </>
                          )}
                        </div>
                      </FormProvider>


                    </div>
                  </div>
                </div>
              </Card>
              <FixedFooter
                onCancel={onCancel}
                onSubmit={handleSubmit(handlePaymentUpdate)}
                loading={isLoading}
                buttonLabel="Update Payment"
              />
            </>
          )
        }

      </div>

      <RightSideModal isOpen={modals.viewAll} onClose={() => toggleModal('viewAll')} title="Dues & Transactions" isFooterHidden={true} maxWidth={480}>
        <Card className="p-0 no-bg">
          <DynamicTabs
            tabs={[
              { id: "transactions", label: "Transactions", onClick: handleTabChange, content: renderTransactionsContent() },
            ]}
            defaultActiveTab="dues"
          />
        </Card>
      </RightSideModal>
    </>
  );
};

export default EditReceivable;